import { useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Sidebar,
  TrashIcon,
  AlertIcon,
  SuccessIcon,
  LockIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { keycloakApi } from '@/api/keycloak';
import { getAvatarColor, getInitials, toTitleCase } from '@/utils/userDisplay';

interface UserDetailPanelProps {
  user: {
    id: string;
    objectId: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    mfaEnabled: boolean;
    loginMethod: string;
    userType: string;
    status: string;
    roles: string[];
    groups: string[];
    createdAt: string;
    lastLogin: string | undefined;
  };
  allGroups: Array<{ id: string; name: string }>;
  onClose: () => void;
  onRemoveGroup: (userId: string, groupId: string) => void;
  onSuspendUser: (userId: string) => void;
}

const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const UserDetailPanel = ({
  user,
  allGroups,
  onClose,
  onRemoveGroup,
  onSuspendUser,
}: UserDetailPanelProps) => {
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);

  const { mutate: removeFromGroup } = useApiMutation<
    { userId: string; groupId: string },
    void
  >({
    mutationFn: async (data) => {
      await keycloakApi.removeUserFromGroup(data.groupId, data.userId);
    },
    successMessage: 'User removed from group',
    errorMessage: 'Failed to remove user from group',
    onSuccess: () => {
      // Will be called after API succeeds — parent handles reload
    },
  });

  const { mutate: suspendUser, isSubmitting: isSuspending } = useApiMutation<
    string,
    void
  >({
    mutationFn: async (userId) => {
      await keycloakApi.updateUser(userId, { enabled: false });
    },
    successMessage: 'User suspended successfully',
    errorMessage: 'Failed to suspend user',
    onSuccess: () => {
      // Parent handles reload
    },
  });

  const handleRemoveGroup = async (userId: string, groupId: string) => {
    await removeFromGroup({ userId, groupId });
    onRemoveGroup(userId, groupId);
  };

  const handleSuspendUser = async (userId: string) => {
    await suspendUser(userId);
    onSuspendUser(userId);
  };

  const getGroupName = (groupId: string) => {
    return allGroups.find((g) => g.id === groupId)?.name || groupId;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Sidebar
      visible
      position="right"
      backdrop
      overlay
      animated={false}
      showClose
      onClose={onClose}
      header={<span className="font-bold text-sm">User Details</span>}
      className="!w-[480px]"
    >
      <div className="p-4">
        {/* User Identity Section */}
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <Avatar
              text={getInitials(user.firstName, user.lastName, user.email)}
              size="lg"
              aria-label={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email}
              style={getAvatarColor(user.objectId || user.email)}
            />
            <div className="min-w-0">
              <h3 className="text-xl font-semibold truncate">
                {toTitleCase(user.firstName)} {toTitleCase(user.lastName)}
              </h3>
              <p className="text-sm text-color-tertiary truncate">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge color={user.status === 'active' ? 'green' : 'orange'}>
              {capitalizeFirst(user.status)}
            </Badge>
            <Badge color={user.userType === 'internal' ? 'blue' : 'gray'}>
              {capitalizeFirst(user.userType)}
            </Badge>
          </div>
        </div>

        <Divider />

        {/* User Details Grid */}
        <div className="grid grid-cols-2 gap-4 text-sm py-4">
          <div className="min-w-0">
            <p className="text-color-tertiary">Username</p>
            <p className="font-medium truncate" title={user.username}>
              {user.username}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-color-tertiary">Login Method</p>
            <p className="truncate" title={user.loginMethod}>{user.loginMethod}</p>
          </div>
          <div>
            <p className="text-color-tertiary">Created</p>
            <p>{formatDate(user.createdAt)}</p>
          </div>
          <div>
            <p className="text-color-tertiary">Last Login</p>
            <p>{formatDate(user.lastLogin)}</p>
          </div>
          <div>
            <p className="text-color-tertiary">MFA Status</p>
            <div className="flex items-center gap-1">
              {user.mfaEnabled ? (
                <>
                  <SuccessIcon className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Enabled</span>
                </>
              ) : (
                <>
                  <AlertIcon className="w-4 h-4 text-orange-500" />
                  <span className="text-orange-500">Not Enabled</span>
                </>
              )}
            </div>
          </div>
          <div>
            <p className="text-color-tertiary">Object ID</p>
            <p className="font-mono text-xs break-all">{user.objectId}</p>
          </div>
        </div>

        <Divider />

        {/* Groups Section */}
        <div className="py-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <LockIcon className="w-4 h-4" />
            Groups
          </h4>

          {user.groups.length === 0 ? (
            <p className="text-sm text-color-tertiary italic">No groups assigned</p>
          ) : (
            <div className="flex flex-col gap-1">
              {user.groups.map((groupId) => (
                <div
                  key={groupId}
                  className="flex items-center justify-between py-1.5 px-2 bg-gray-50 dark:bg-gray-800 rounded"
                >
                  <span className="text-sm">{getGroupName(groupId)}</span>
                  <Button
                    variant="destructive-secondary"
                    icon={<TrashIcon />}
                    aria-label={`Remove from ${getGroupName(groupId)}`}
                    onClick={() => handleRemoveGroup(user.id, groupId)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* Danger Zone Section */}
        <div className="py-4">
          <h4 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
            <AlertIcon className="w-4 h-4" />
            Danger Zone
          </h4>

          {!showSuspendConfirm ? (
            <div>
              <Button
                variant="destructive-secondary"
                onClick={() => setShowSuspendConfirm(true)}
                className="w-full"
              >
                Suspend User Access
              </Button>
              <p className="text-xs text-color-tertiary mt-2">
                Suspending a user will immediately revoke their access to all OneRing
                resources. They will not be able to log in until reactivated by an
                administrator.
              </p>
            </div>
          ) : (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
              <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-3">
                Are you sure you want to suspend {toTitleCase(user.firstName)}{' '}
                {toTitleCase(user.lastName)}?
              </p>
              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  onClick={() => setShowSuspendConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={() => {
                    handleSuspendUser(user.id);
                    setShowSuspendConfirm(false);
                  }}
                  disabled={isSuspending}
                >
                  {isSuspending ? 'Suspending...' : 'Confirm Suspend'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default UserDetailPanel;
